import classNames from "classnames";
import { useEffect, useState, type MouseEvent, type ReactNode } from "react";

import { useViewContext } from "@editor-providers";
import { ButtonElement } from "../Button.styles";
import useGetSearchParams from "../hooks/useGetSearchParams";
import { useSealValidation } from "../hooks/useSealValidation";
import { Check } from "../icons/Check";
import { Signature } from "../icons/Signature";
import type { AcceptResponse, DefaultProps } from "../types";
import { Summary } from "./Summary";
import { Popup } from "./popup/Popup";

const ButtonIcon = ({ iconType }: { iconType?: "signature" | "check" }) =>
  iconType === "check" ? (
    <Check fill="currentColor" style={{ flexShrink: 0 }} />
  ) : (
    <Signature style={{ flexShrink: 0, width: "1em", height: "1em" }} />
  );

const ButtonContent = ({
  editableContent,
  title,
}: {
  editableContent?: ReactNode;
  title: string;
}) => editableContent ?? <span dangerouslySetInnerHTML={{ __html: title }} />;

const ButtonBody = ({
  settings,
  editableContent,
}: {
  settings: DefaultProps;
  editableContent?: ReactNode;
}) => {
  if (settings.showDottedLine) {
    return (
      <>
        <ButtonIcon iconType={settings.iconType} />
        <span className="button-dotted-line button-dotted-line--abs" />
      </>
    );
  }

  if (settings.showIcon) {
    return (
      <span className="button-icon-content">
        <ButtonIcon iconType={settings.iconType} />
        <ButtonContent
          editableContent={editableContent}
          title={settings.title}
        />
      </span>
    );
  }

  return (
    <ButtonContent editableContent={editableContent} title={settings.title} />
  );
};

export const Element = ({
  settings,
  editableContent,
  setSubmittedState,
}: {
  id: string;
  settings: DefaultProps;
  editableContent?: ReactNode;
  setSubmittedState: () => void;
}) => {
  "use no memo";
  const [openPopup, setOpenPopup] = useState(false);
  const { validateSeal, status } = useSealValidation();
  const searchParamsData = useGetSearchParams();
  const [summary, setSummary] = useState<AcceptResponse | null>(null);
  const { view } = useViewContext();

  useEffect(() => {
    const getSummary = async () => {
      const [seal] = await validateSeal();
      if (seal) {
        setSummary(seal);
        if (setSubmittedState) setSubmittedState();
      }
    };
    if (searchParamsData && searchParamsData.sealPDF) {
      setSummary({
        name: searchParamsData.name ?? "",
        email: searchParamsData.email ?? "",
        role: searchParamsData.role ?? "",
        timestamp: searchParamsData.timestamp ?? "",
      });
      if (setSubmittedState) setSubmittedState();
    } else {
      getSummary();
    }
  }, []);

  const setResult = (result: AcceptResponse[]) => {
    const [seal] = result;
    if (seal) {
      setSummary(seal);
      if (setSubmittedState) setSubmittedState();
    }
  };

  return (
    <>
      {summary ? (
        <Summary {...summary} />
      ) : (
        <ButtonElement
          className={classNames("buttonElement", status)}
          {...STYLED_ATTRIBUTES({ settings })}
          data-analytics-click-event={settings.analyticsClickEvent}
          onClick={(e: MouseEvent) =>
            view === "editor" ? e.preventDefault() : setOpenPopup(true)
          }
        >
          {status === "loading" ? (
            <div className="buttonWrapper">
              <span className="accept-spinner" />
            </div>
          ) : (
            <div
              className={classNames("buttonWrapper", {
                "tw-shadow-xs": settings.showIcon && !settings.showDottedLine,
                "buttonWrapper--pad": settings.showDottedLine,
              })}
            >
              <ButtonBody
                settings={settings}
                editableContent={editableContent}
              />
            </div>
          )}
        </ButtonElement>
      )}
      {openPopup ? (
        <Popup closePopup={() => setOpenPopup(false)} setResult={setResult} />
      ) : null}
    </>
  );
};

const STYLED_ATTRIBUTES = ({ settings }: { settings: DefaultProps }) => ({
  $color: settings.color,
  $background: settings.background,
  $colorHover: settings.colorHover,
  $backgroundHover: settings.backgroundHover,
  $round: settings.round,
  $fontSize: settings.fontSize,
  $horizontalAlignment: settings.horizontalAlignment,
  $borderWidth: settings.borderWidth,
  $borderColor: settings.borderColor,
  $borderColorHover: settings.borderColorHover,
  $isBold: settings.isBold,
});
